import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, ProgressBar, Spinner } from 'react-bootstrap';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { auth } from '../../firebase';
import Icon from '../UI/Icon';
import ModuleIntroModal from './ModuleIntroModal';
import { getAllUserProgress, getCompletedModules, saveModuleCompletion, getUserModuleSections } from '../../services/moduleProgressService';

function ModuleOverview({ onModuleSelect }) {
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completedModules, setCompletedModules] = useState(new Set());
  const [progressUpdateTrigger, setProgressUpdateTrigger] = useState(0);
  const [userProgress, setUserProgress] = useState({});
  const [userData, setUserData] = useState({});

  useEffect(() => {
    // Fetch user data and determine if intro modal should be shown
    fetchUserData();
    // Load completed modules from localStorage
    loadCompletedModules();
    // Note: Learning modules are now fetched via real-time listener
  }, []);

  // Add effect to refresh progress when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setProgressUpdateTrigger(prev => prev + 1);
        // Also reload completed modules to ensure latest state
        loadCompletedModules();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Add effect to listen for real-time module order changes
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'learning_modules'),
      (snapshot) => {
        if (snapshot.empty) {
          setModules(getDefaultModules());
        } else {
          const firebaseModules = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            firebaseModules.push({
              id: data.order || firebaseModules.length + 1,
              order: data.order,
              title: data.title,
              description: data.description,
              totalLessons: data.subsections ? data.subsections.length : 4,
              completedLessons: 0,
              iconName: 'target',
              color: getModuleColor(data.order || firebaseModules.length + 1),
              subsections: data.subsections || [],
              checklist: data.checklist || [],
              firebaseId: doc.id
            });
          });
          
          // Sort by order to ensure correct sequence
          firebaseModules.sort((a, b) => (a.order || 0) - (b.order || 0));
          setModules(firebaseModules);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to module order changes:', error);
        // Fallback to static fetch on error
        fetchLearningModules();
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // Add effect to reload completed modules when progress trigger changes
  useEffect(() => {
    if (progressUpdateTrigger > 0) {
      loadCompletedModules();
    }
  }, [progressUpdateTrigger]);


  const loadCompletedModules = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        // Load from Firebase
        const firebaseCompleted = await getCompletedModules(user.uid);
        setCompletedModules(new Set(firebaseCompleted));
        
        // Also load user progress for step completion tracking
        const progress = await getAllUserProgress(user.uid);
        setUserProgress(progress.modules || {});
        
        // Save to localStorage for offline access
        localStorage.setItem('completedModules', JSON.stringify(firebaseCompleted));
      } else {
        // Fallback to localStorage if no user
        const saved = localStorage.getItem('completedModules');
        if (saved) {
          const completedArray = JSON.parse(saved);
          setCompletedModules(new Set(completedArray));
        }
      }
    } catch (error) {
      console.error('Error loading completed modules:', error);
      // Fallback to localStorage on error
      const saved = localStorage.getItem('completedModules');
      if (saved) {
        const completedArray = JSON.parse(saved);
        setCompletedModules(new Set(completedArray));
      }
    }
  };

  const saveCompletedModules = (completedSet) => {
    try {
      const completedArray = Array.from(completedSet);
      localStorage.setItem('completedModules', JSON.stringify(completedArray));
    } catch (error) {
      console.error('Error saving completed modules:', error);
    }
  };

  const markModuleCompleted = async (moduleOrder) => {
    const updatedCompleted = new Set(completedModules);
    updatedCompleted.add(moduleOrder);
    
    // Update state immediately
    setCompletedModules(updatedCompleted);
    
    // Force a progress update to trigger re-renders
    setProgressUpdateTrigger(prev => prev + 1);
    
    // Save to localStorage immediately
    saveCompletedModules(updatedCompleted);
    
    // Save to Firebase
    const user = auth.currentUser;
    if (user) {
      try {
        await saveModuleCompletion(user.uid, moduleOrder);
      } catch (error) {
        console.error('Error saving module completion to Firebase:', error);
      }
    }
  };


  const fetchLearningModules = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'learning_modules'));
      
      if (querySnapshot.empty) {
        // Fallback to hardcoded modules if no Firebase data
        setModules(getDefaultModules());
      } else {
        const firebaseModules = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          firebaseModules.push({
            id: data.order || firebaseModules.length + 1,
            order: data.order, // Keep the original order field
            title: data.title,
            description: data.description,
            totalLessons: data.subsections ? data.subsections.length : 4,
            completedLessons: 0, // This will be determined by completion status
            iconName: 'target',
            color: getModuleColor(data.order || firebaseModules.length + 1),
            // Store complete Firebase data for potential future use
            subsections: data.subsections || [],
            checklist: data.checklist || [],
            firebaseId: doc.id
          });
        });
        
        // Sort by order
        firebaseModules.sort((a, b) => (a.order || 0) - (b.order || 0));
        setModules(firebaseModules);
      }
    } catch (error) {
      console.error('Error fetching learning modules:', error);
      // Fallback to hardcoded modules on error
      setModules(getDefaultModules());
    } finally {
      setLoading(false);
    }
  };

  const getModuleColor = (order) => {
    const colors = ['#B2D8D8', '#FFB7B2', '#FFF3CD', '#D4E6F1', '#E8DAF6'];
    return colors[(order - 1) % colors.length];
  };

  const getDefaultModules = () => [
    {
      id: 1,
      order: 0,
      title: 'Wahrnehmung',
      description: 'Wir beginnen mit der Wahrnehmung der Laute und deren Unterscheidung',
      totalLessons: 7,
      completedLessons: 7,
      iconName: 'check',
      color: '#B2D8D8',
      subsections: [],
      checklist: [],
      firebaseId: 'default-1'
    },
    {
      id: 2,
      order: 1,
      title: 'Produktion',
      description: 'Lernen Sie die korrekte Artikulation und Produktion der Laute',
      totalLessons: 5,
      completedLessons: 2,
      iconName: 'clock',
      color: '#FFB7B2',
      subsections: [],
      checklist: [],
      firebaseId: 'default-2'
    },
    {
      id: 3,
      order: 2,
      title: 'Transfer Übungen',
      description: 'Übertragen Sie das Gelernte in den Alltag mit praktischen Übungen',
      totalLessons: 4,
      completedLessons: 0,
      iconName: 'target',
      color: '#FFF3CD',
      subsections: [],
      checklist: [],
      firebaseId: 'default-3'
    }
  ];

  const fetchUserData = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          // Only show intro modal if user hasn't seen it before
          if (!data.hasSeenModuleIntroModal) {
            setShowIntroModal(true);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // On error, don't show the modal
    }
  };

  const handleCloseIntroModal = async () => {
    setShowIntroModal(false);
    // Save that the user has seen the modal to the database
    try {
      const user = auth.currentUser;
      if (user && !userData.hasSeenModuleIntroModal) {
        await updateDoc(doc(db, 'users', user.uid), {
          hasSeenModuleIntroModal: true
        });
        // Update local state
        setUserData(prev => ({ ...prev, hasSeenModuleIntroModal: true }));
      }
    } catch (error) {
      console.error('Error updating hasSeenModuleIntroModal:', error);
    }
  };


  const getProgressPercentage = (module) => {
    // Check if module is marked as fully completed
    if (completedModules.has(module.order)) {
      return 100;
    }
    
    // Calculate actual progress based on completed lessons
    const totalLessons = module.subsections ? module.subsections.length : module.totalLessons;
    if (totalLessons === 0) return 0;
    
    const completedLessons = getActualCompletedLessons(module);
    return (completedLessons / totalLessons) * 100;
  };

  const getActualCompletedLessons = (module) => {
    // Use progressUpdateTrigger to ensure recalculation when progress changes
    void progressUpdateTrigger;
    
    if (!module.subsections || module.subsections.length === 0) {
      return 0;
    }
    
    // Try to get completed sections from Firebase progress
    if (userProgress[module.id] && userProgress[module.id].sections) {
      return userProgress[module.id].sections.length;
    }
    
    // Fallback: count completed subsections by checking individual steps
    let completedCount = 0;
    module.subsections.forEach((subsection) => {
      if (subsection.items && subsection.items.length > 0) {
        // Try Firebase progress first, then fallback to localStorage
        let completedSteps = [];
        
        if (userProgress[module.id] && userProgress[module.id].steps) {
          completedSteps = userProgress[module.id].steps;
        } else {
          // Fallback to localStorage
          const stepKey = `completedSteps_module_${module.id}`;
          const saved = localStorage.getItem(stepKey);
          if (saved) {
            completedSteps = JSON.parse(saved);
          }
        }
        
        // Check if all items in this subsection are completed
        const allItemsCompleted = subsection.items.every(item => {
          return completedSteps.includes(item.id);
        });
        
        if (allItemsCompleted) {
          completedCount++;
        }
      }
    });
    
    return completedCount;
  };

  const isModuleLocked = (module) => {
    // First module (order 0) is always unlocked
    if (module.order === 0 || module.order === undefined) {
      return false;
    }
    
    // Check if previous module is completed
    const previousOrder = module.order - 1;
    const isLocked = !completedModules.has(previousOrder);
    
    return isLocked;
  };

  const getModuleIcon = (module) => {
    const percentage = getProgressPercentage(module);
    if (percentage === 100) return 'check';
    if (percentage > 0) return 'clock';
    return module.iconName;
  };

  const getStatusBadge = (module) => {
    if (isModuleLocked(module)) return { text: 'Gesperrt', variant: 'secondary' };
    
    const percentage = getProgressPercentage(module);
    if (percentage === 0) return { text: 'Nicht begonnen', variant: 'secondary' };
    if (percentage === 100) return { text: 'Abgeschlossen', variant: 'success' };
    return { text: 'In Bearbeitung', variant: 'warning' };
  };

  const resetAllProgress = async () => {
    if (!window.confirm('WARNUNG: Dies wird ALLE Fortschritte in ALLEN Modulen löschen! Sind Sie sicher?')) {
      return;
    }

    try {
      // Clear all localStorage items related to module progress
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('completedSteps_') || key.startsWith('checklistItems_') || key === 'completedModules')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear Firebase data
      const user = auth.currentUser;
      if (user) {
        // Reset user_progress document
        await setDoc(doc(db, 'user_progress', user.uid), {
          modules: {},
          completedModules: [],
          updatedAt: new Date()
        });

        // Reset hasSeenModuleIntroModal in user document (optional)
        // await updateDoc(doc(db, 'users', user.uid), {
        //   hasSeenModuleIntroModal: false
        // });
      }

      // Reset component state
      setCompletedModules(new Set());
      setUserProgress({});
      setProgressUpdateTrigger(prev => prev + 1);
      
      alert('Alle Fortschritte wurden erfolgreich zurückgesetzt!');
      
      // Reload the modules
      await loadCompletedModules();
      
    } catch (error) {
      console.error('Error resetting progress:', error);
      alert('Fehler beim Zurücksetzen der Fortschritte: ' + error.message);
    }
  };


  return (
    <>
      <ModuleIntroModal 
        show={showIntroModal} 
        onClose={handleCloseIntroModal}
      />
      
      <Container>
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Lernmodule</h2>
          </div>
          <p className="text-secondary">
            Wähle ein Modul aus, um mit dem Training zu beginnen. Jedes Modul enthält Theorie, Videos und praktische Übungen.
          </p>
        </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2 text-secondary">Loading learning modules...</p>
        </div>
      ) : (
        <Row className="g-4">
          {modules.map(module => {
          const status = getStatusBadge(module);
          const locked = isModuleLocked(module);
          
          return (
            <Col key={module.id} md={6} lg={4}>
              <Card className={`module-card h-100 ${locked ? 'module-locked' : ''}`}>
                <Card.Body className="d-flex flex-column">
                  <div className="module-header mb-3">
                    <div 
                      className={`module-icon ${locked ? 'module-icon-locked' : `module-icon-${getModuleIcon(module)}`}`}
                      style={{ backgroundColor: locked ? '#E0E0E0' : `${module.color}20` }}
                    >
                      <Icon name={locked ? 'lock' : getModuleIcon(module)} size="xl" />
                    </div>
                    <Badge bg={status.variant} className="ms-auto">
                      {status.text}
                    </Badge>
                  </div>
                  
                  <h4 className="mb-2">{module.title}</h4>
                  <p className="text-secondary flex-grow-1">{module.description}</p>
                  
                  <div className="module-progress mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-muted">Fortschritt</small>
                      <small className="text-muted">
                        {locked ? 0 : getActualCompletedLessons(module)} / {module.subsections ? module.subsections.length : module.totalLessons} Lektionen
                      </small>
                    </div>
                    <ProgressBar 
                      now={locked ? 0 : (module.subsections ? (getActualCompletedLessons(module) / module.subsections.length) * 100 : getProgressPercentage(module))} 
                      style={{ height: '6px' }}
                      disabled={locked}
                    />
                  </div>
                  
                  <Button 
                    variant={locked ? "secondary" : (getProgressPercentage(module) === 100 ? "success" : (getProgressPercentage(module) === 0 ? "primary" : "outline-primary"))}
                    className="w-100"
                    onClick={() => !locked && onModuleSelect(module.id, module, markModuleCompleted)}
                    disabled={locked}
                  >
                    {locked ? 'Vorheriges Modul abschließen' : (getProgressPercentage(module) === 100 ? 'Abgeschlossen ✓' : (getProgressPercentage(module) === 0 ? 'Modul starten' : 'Weiter lernen'))}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
        </Row>
      )}
      </Container>
    </>
  );
}

export default ModuleOverview;